import * as React from 'react';
import { useRef } from 'react';
import { view, createViewConfig, dynamic, assignProps } from '@sail/engine';
import { checkCircleFilled } from '@sail/icons/react/Icon';
import { isFocusVisible, useOption, useItem, useCollectionState } from '@sail/react-aria';
import { Icon, IconConfig } from './Icon';
import { css } from '../css';
/**
 * These icon categories are ones that we feel need to be shown
 * slightly larger, due to their low visual weight at 12px
 */ const SPECIAL_ICON_CATEGORIES = [
    'flag',
    'brand',
    'card',
    'paymentmethod'
];
export const ListBoxItemTransforms = {
    defaultField: 'textValue',
    makeGenericItem (props) {
        const textValue = props.textValue || (typeof props.children === 'string' ? props.children : props.id);
        return {
            'aria-label': props['aria-label'] || textValue,
            textValue,
            data: {
                icon: props.icon,
                children: props.children
            }
        };
    }
};
export const ListBoxItemConfig = createViewConfig({
    name: 'ListBoxItem',
    props: {}
});
export const ListBoxItem = ListBoxItemConfig.createView((allProps)=>{
    const { id, children, textValue, subviews, icon, description, // `data` is not used anywhere right now
    data, ...props } = allProps;
    const key = useItem(id, ListBoxItemTransforms.makeGenericItem({
        id,
        'aria-label': props['aria-label'],
        children,
        textValue,
        icon
    }));
    const state = useCollectionState();
    const tree = state;
    const ref = useRef(null);
    const { optionProps, isFocused, isSelected, isDisabled, labelProps, descriptionProps } = useOption({
        key
    }, state, ref);
    return /*#__PURE__*/ React.createElement(view.div, {
        ...props,
        ref: ref,
        uses: [
            assignProps(optionProps),
            isFocusVisible() && isFocused ? css({
                focusRing: 'focus',
                // create new stacking context to allows the focus ring to appear on top
                // of the potential hover state in the item below it, without resorting to
                // bumping the zIndex
                position: 'relative'
            }) : css({
                // focus ring will only apply when focus-visible and focused
                focusRing: 'none'
            })
        ]
    }, /*#__PURE__*/ React.createElement(IconConfig.Provider, {
        value: {
            css: isDisabled ? css({
                opacity: '30%'
            }) : css({}),
            uses: [
                // certain categories of icons are too small at 12px, so we bump them up to 16px
                dynamic(({ icon })=>{
                    const result = [];
                    if (SPECIAL_ICON_CATEGORIES.includes(icon.category)) {
                        result.push(css({
                            width: 'medium',
                            height: 'medium'
                        }));
                    }
                    return result;
                }),
                // irrespective of the icon's size, provision the same space
                css({
                    flexBasis: '16px'
                })
            ]
        }
    }, icon, !icon && tree.expandable ? /*#__PURE__*/ React.createElement(view.span, {
        role: "presentation",
        css: {
            width: 'medium'
        }
    }) : null, description ? /*#__PURE__*/ React.createElement(view.span, {
        inherits: subviews.content
    }, /*#__PURE__*/ React.createElement(view.span, {
        ...labelProps,
        inherits: subviews.label
    }, children), /*#__PURE__*/ React.createElement(view.span, {
        ...descriptionProps,
        css: [
            css({
                font: 'label.small',
                color: 'subdued',
                fontWeight: 'normal'
            }),
            isDisabled ? css({
                color: 'form.disabled'
            }) : css({})
        ],
        inherits: subviews.description
    }, description)) : children), /*#__PURE__*/ React.createElement(Icon, {
        icon: checkCircleFilled,
        role: "img",
        css: [
            isSelected ? css({
                opacity: 1
            }) : css({
                opacity: 0
            })
        ],
        inherits: subviews.checked
    }));
}, {
    css: {
        stack: 'x',
        font: 'label.medium',
        color: 'form',
        gap: 'space.75',
        borderRadius: 'xsmall',
        paddingY: 'space.75',
        paddingX: 'space.125',
        alignY: 'baseline',
        alignX: 'start',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: 'offset'
        },
        '[aria-disabled="true"]': {
            color: 'form.disabled',
            cursor: 'default'
        },
        '[aria-disabled="true"]:hover': {
            backgroundColor: 'form'
        }
    },
    subviews: {
        checked: {
            css: {
                pointerEvents: 'none',
                transition: 'opacity 150ms',
                marginLeft: 'auto'
            }
        },
        content: {
            css: {
                stack: 'y',
                // Prevents selection and cursor changes and event overrides
                pointerEvents: 'none'
            }
        }
    },
    uses: [
        IconConfig.customize({
            defaults: {
                size: 'xsmall'
            }
        })
    ]
});

//# sourceMappingURL=ListBoxItem.js.map